import { Fragment, useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Briefcase,
  Download,
  ListChecks,
  Plus,
  ShieldCheck,
} from 'lucide-react';
import { useHubProjects, useLinkHubProjectBulk } from '../hub/api';
import { HubProjectPicker } from './HubProjectPicker';
import {
  ArchivedToggle,
  Button,
  buttonVariants,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  PageHeader,
  SearchInput,
  SegmentedControl,
  Select,
  SkeletonTable,
  groupRows,
  sortRows,
  type DataTableSort,
  Modal,
  toast,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatHours, formatMoney } from '../../lib/format';
import {
  useArchiveAgreements,
  useAgreementClients,
  useAgreements,
  type AgreementRow,
  type AgreementType,
} from './api';
import { csvFilename } from '../../lib/csv';
import { downloadCsv, orderedRollupSources } from './components';
import {
  AgreementMobileCard,
  CLIENT_GROUP,
  AgreementsTable,
  AgreementsTotalsCard,
  agreementColumns,
  rollupAgreements,
} from './AgreementsTable';
import { AgreementModal } from './AgreementModal';
import { needsAttention, verdictClass, verdictScore } from './verdict';

const TYPE_PILLS: Array<{ value: AgreementType | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'PROJECT', label: 'Agreements' },
  { value: 'SUPPORT', label: 'Support' },
  { value: 'RECURRING', label: 'Recurring' },
  { value: 'INTERNAL', label: 'Internal' },
];

const PROJECT_TYPES = TYPE_PILLS.map((p) => p.value).filter(Boolean) as AgreementType[];
const SORT_KEYS = ['name', 'status', 'type', 'hours', 'revenue', 'cost', 'burn', 'margin'] as const;
/**
 * Trouble first, on arrival.
 *
 * The list opened on biggest-revenue-first, which answers a question nobody
 * asks when they land here: the three hosting agreements running at −606.6%,
 * −178.2% and −64.2% margin were rows 15, 16 and 17 of 18, below the fold,
 * while the healthiest agreement in the portfolio held row 1. Severity descending
 * (see verdict.ts#verdictScore) puts the money on fire at the top and internal
 * overhead at the bottom. Sort is still URL-driven, so ?sort=revenue&dir=desc
 * restores the old view and is shareable.
 */
const DEFAULT_SORT: DataTableSort = { key: 'status', dir: 'desc' };

export function AgreementsPage() {
  const navigate = useNavigate();
  // Every filter, the search box and the sort live in the URL. A view of this
  // screen is therefore a link: shareable, bookmarkable, and restored by the
  // back button. `/agreements?q=zzzznone` used to render the unfiltered list with
  // an empty search box.
  const [params, setParams] = useSearchParams();

  const rawType = params.get('type') ?? '';
  const type: AgreementType | '' = PROJECT_TYPES.includes(rawType as AgreementType)
    ? (rawType as AgreementType)
    : '';
  const clientId = params.get('client') ?? '';
  const attention = params.get('attention') === '1';
  const search = params.get('q') ?? '';
  const showArchived = params.get('archived') === '1';
  const rawSort = params.get('sort') ?? '';
  const sort: DataTableSort = SORT_KEYS.includes(rawSort as (typeof SORT_KEYS)[number])
    ? { key: rawSort, dir: params.get('dir') === 'asc' ? 'asc' : 'desc' }
    : DEFAULT_SORT;

  const patch = useCallback(
    (next: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(next)) {
            if (value == null || value === '') out.delete(key);
            else out.set(key, value);
          }
          return out;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const clearFilters = () =>
    patch({ q: null, type: null, client: null, archived: null, attention: null });

  const [modalOpen, setModalOpen] = useState(false);
  /** Bulk selection (Jesse, 2026-08-20: "I need a way to bulk archive
   *  agreements"). Ids only — the rows they refer to re-derive per render, so a
   *  filter change never strands a selection on rows no longer in view. */
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const [bulkLinkOpen, setBulkLinkOpen] = useState(false);
  const [bulkChoice, setBulkChoice] = useState('');
  const archiveBatch = useArchiveAgreements();
  const bulkLink = useLinkHubProjectBulk();
  const hub = useHubProjects();
  /** Inline "no budget → set one" flow: opens the edit modal on that agreement. */
  const [budgetTarget, setBudgetTarget] = useState<AgreementRow | null>(null);

  // Ask for everything and hide the archived ones locally, so the toggle can
  // print how many there are — a "show archived" control that cannot say
  // whether there is anything to show is a question mark, not a control.
  const query = useAgreements({ type, clientId, showArchived: true });
  const clientsQuery = useAgreementClients();

  const fetched = query.data?.agreements ?? [];
  const archivedCount = fetched.filter((p) => !p.isActive).length;

  /** Everything the type/client/search filters allow — the chip's denominator. */
  const matched = useMemo(() => {
    const all = (query.data?.agreements ?? []).filter((p) => showArchived || p.isActive);
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.client.name.toLowerCase().includes(q) ||
        (p.code ?? '').toLowerCase().includes(q),
    );
  }, [query.data, search, showArchived]);

  /**
   * "Needs attention" = the verdict says someone has to act: LOSING MONEY,
   * AT RISK (over the lifetime budget/fee) or THIN MARGIN. WATCH is deliberately
   * out — it is a heads-up, not a fire, and a chip that flags half the portfolio
   * flags nothing.
   */
  const attentionCount = useMemo(
    () => matched.filter((p) => needsAttention(p.metrics)).length,
    [matched],
  );

  const rows = useMemo(
    () => (attention ? matched.filter((p) => needsAttention(p.metrics)) : matched),
    [matched, attention],
  );

  // Mobile cards get the desktop table's default ordering (worst verdict first)
  // so the two layouts tell the same story in the same order.
  // Grouped by client, clients A→Z, exactly as the table groups them.
  const mobileRows = useMemo(
    () =>
      groupRows(
        [...rows].sort((a, b) => verdictScore(b.metrics) - verdictScore(a.metrics)),
        CLIENT_GROUP,
      ),
    [rows],
  );

  /** Client work and internal overhead totalled separately — see rollupAgreements. */
  const totals = useMemo(() => rollupAgreements(rows), [rows]);

  const exportCsv = () => {
    // Export what is on screen: the filters already narrowed `rows`, and the
    // order comes from the table's OWN comparator so the file cannot disagree
    // with the tbody about "worst margin first".
    const ordered = groupRows(sortRows(rows, agreementColumns({ showClient: true }), sort), CLIENT_GROUP);
    downloadCsv(
      csvFilename({
        entity: 'agreements',
        view: [type, attention ? 'needs-attention' : '', showArchived ? 'with-archived' : '', search ? `q-${search}` : '']
          .filter(Boolean)
          .join('-'),
      }),
      [
        [
          'Agreement',
          'Client',
          'Code',
          'Status',
          'Type',
          'Basis',
          'Revenue source',
          'Hours',
          'Revenue',
          'Cost',
          'Margin %',
          'Budget used',
          'Budget',
          'Budget %',
          'Budget period',
          'Lifetime used',
          'Lifetime budget',
          'Lifetime %',
          'Prepaid hours',
          'Prepaid used',
          'Prepaid left',
          'Prepaid not sent yet',
        ],
        ...ordered.map((p) => {
          const burn = p.metrics.burn;
          return [
            p.name,
            p.client.name,
            p.code ?? '',
            verdictClass(p.metrics).label,
            p.type,
            p.metrics.basis,
            p.metrics.revenueSource,
            p.metrics.hours.toFixed(2),
            p.metrics.revenue.toFixed(2),
            p.metrics.cost.toFixed(2),
            p.metrics.margin != null ? (p.metrics.margin * 100).toFixed(1) : '',
            burn ? burn.used.toFixed(2) : '',
            burn ? burn.budget.toFixed(2) : '',
            burn ? (burn.pct * 100).toFixed(1) : '',
            burn ? (burn.periodMonth ?? 'all time') : '',
            burn?.lifetime ? burn.lifetime.used.toFixed(2) : '',
            burn?.lifetime ? burn.lifetime.budget.toFixed(2) : '',
            burn?.lifetime ? (burn.lifetime.pct * 100).toFixed(1) : '',
            p.prepaid ? p.prepaid.purchased.toFixed(2) : '',
            p.prepaid ? p.prepaid.burnt.toFixed(2) : '',
            p.prepaid ? p.prepaid.available.toFixed(2) : '',
            p.prepaid ? p.prepaid.pending.toFixed(2) : '',
          ];
        }),
        [
          `Client work — ${totals.client.count} agreements`,
          '',
          '',
          '',
          '',
          '',
          // The footing names its basis in the column that exists for it —
          // the file must not disagree with the screen about what the total is.
          orderedRollupSources(totals.sources).join('+'),
          totals.client.hours.toFixed(2),
          totals.client.revenue.toFixed(2),
          totals.client.cost.toFixed(2),
          totals.clientMargin != null ? (totals.clientMargin * 100).toFixed(1) : '',
        ],
        [
          `Internal — ${totals.internal.count} agreements`,
          '',
          '',
          '',
          '',
          '',
          '',
          totals.internal.hours.toFixed(2),
          '',
          totals.internal.cost.toFixed(2),
          '',
        ],
      ],
    );
  };

  // Counted over the rows actually shown, so the subtitle can never claim
  // "18 active" above a table filtered down to none.
  const activeCount = rows.filter((p) => p.isActive).length;
  const filtered = !!(search || type || clientId || attention);
  /** Failed at least once but TanStack is still retrying — not "loading". */
  const retrying = query.isPending && query.failureCount > 0;
  const failureMessage =
    query.error instanceof Error
      ? query.error.message
      : query.failureReason instanceof Error
        ? query.failureReason.message
        : undefined;

  /**
   * The subtitle describes the ORG when nothing is filtered and the FILTER when
   * something is. `/agreements?q=zzz` used to rewrite it to "0 active · 0.00
   * hours · $0 client revenue", which reads as "this business has no agreements"
   * rather than "this filter matched nothing".
   */
  const subtitle = (() => {
    if (query.isPending || query.isError || retrying) return undefined;
    /**
     * The rollup names its basis, because it has up to three of them.
     *
     * It read "$1,017,959 client revenue" — one word for contract value plus
     * hours × rate plus invoiced, and "revenue" is now the app's word for CASH
     * ($1,004,839 on the dashboard, one click away). The rows were always
     * honest (REVENUE_SOURCE_TAG under every figure); only the total was not.
     *
     * ⚠ **THE NOUN CARRIES IT NOW, NOT A TRAILING CLAUSE.** A page subtitle is
     * the one line a reader takes in before anything else, and this one ended
     * *"— hours × rate + invoiced, not cash collected"*: a definition, in the
     * position of a summary (Jesse, 2026-08-13: *"don't need as much
     * explanation on agreements page"*). **"client work valued"** already says
     * the substance, and the enumeration lives on the footer's (i) — the
     * figure it actually qualifies.
     */
    const figures = `${formatHours(totals.all.hours, { fixed: true })} hours · ${formatMoney(
      totals.client.revenue,
      { whole: true },
    )} client work valued`;
    if (!filtered) {
      // The one number worth leading with: how much of the portfolio is on
      // fire. Sorted to the top by default, so it is also literally what you
      // are looking at.
      const flag =
        attentionCount > 0
          ? ` · ${attentionCount} need${attentionCount === 1 ? 's' : ''} attention`
          : ' · none need attention';
      return `${activeCount} active${flag} · ${figures}`;
    }
    if (rows.length === 0) return 'No agreements match the current filters';
    return `${rows.length} of this org’s agreements match · ${figures}`;
  })();

  return (
    <div>
      <PageHeader
        title="Agreements"
        subtitle={subtitle}
        actions={
          <>
            {/* Labelled, never a bare glyph — two unlabelled icons side by side
                are two guesses, not a toolbar. Both are desk work (a CSV you
                open in a spreadsheet, an org-wide task list), so below `sm`
                they step aside: the phone header carries ONE button and the
                first agreement row rises ~90px up the screen. */}
            <Button
              variant="outline"
              size="sm"
              className="hidden sm:inline-flex sm:px-5 sm:py-2.5"
              onClick={exportCsv}
              disabled={rows.length === 0}
              title="Export CSV"
            >
              <Download className="h-4 w-4" aria-hidden />
              Export CSV
            </Button>
            <Link
              to="/settings?tab=tasks"
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'hidden sm:inline-flex sm:px-5 sm:py-2.5')}
              title="Manage tasks"
            >
              <ListChecks className="h-4 w-4" aria-hidden />
              Manage tasks
            </Link>
            <Button size="sm" className="sm:px-5 sm:py-2.5" onClick={() => setModalOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              New agreement
            </Button>
          </>
        }
      />

      {/* Filter bar. Two rows on a phone, not four: the type pills scroll
          sideways in one row, then client + search + "archived" share the next.
          ONE row from `lg` up — every row of chrome here is an agreement row
          pushed off the screen, and the "Needs attention" chip had to earn its
          place without costing one. */}
      <div className="mb-3 flex flex-wrap items-center gap-2 sm:gap-2.5">
        <SegmentedControl
          ariaLabel="Filter by agreement type"
          size="md"
          scrollable
          className="w-full sm:w-auto"
          value={type}
          onChange={(next) => patch({ type: next || null })}
          options={TYPE_PILLS}
        />
        {/* A second dimension, so it is a chip rather than a seventh type tab:
            it COMPOSES with the type tabs ("Hosting" + "Needs attention") and
            it is the one filter that answers the question this screen exists
            for. The count is live against the current type/client/search, so
            "0" is a real all-clear rather than a dead control. */}
        <button
          type="button"
          aria-pressed={attention}
          title={
            attentionCount === 0
              ? 'Nothing in this view is losing money, over its lifetime budget, or under a 30% margin.'
              : `${attentionCount} agreement${attentionCount === 1 ? '' : 's'} losing money, over the lifetime budget/fee, or under a 30% margin.`
          }
          onClick={() => patch({ attention: attention ? null : '1' })}
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
            attention
              ? 'border-status-late bg-status-late-bg text-status-late'
              : attentionCount > 0
                ? 'border-grey-200 bg-white text-grey-600 hover:border-status-late/50 hover:text-status-late'
                : 'border-grey-200 bg-white text-grey-400 hover:text-grey-600',
          )}
        >
          <AlertTriangle className="h-4 w-4" aria-hidden />
          Needs attention
          <span
            className={cn(
              'num rounded-full px-1.5 py-0.5 text-xs font-bold',
              attention
                ? 'bg-status-late/15'
                : attentionCount > 0
                  ? 'bg-status-late-bg text-status-late'
                  : 'bg-grey-100 text-grey-400',
            )}
          >
            {attentionCount}
          </span>
        </button>
        <Select
          aria-label="Filter by client"
          selectSize="sm"
          className="w-[calc(50%-3.75rem)] sm:w-40"
          options={[
            { value: '', label: 'All clients' },
            // Without "Archived" on, offer only clients with an ACTIVE agreement —
            // picking one whose work is all archived would filter to nothing.
            // The current selection always stays listed, so the control never
            // blanks out under a link that names it.
            ...(clientsQuery.data?.clients ?? [])
              .filter((c) => showArchived || (c.activeAgreements ?? 0) > 0 || c.id === clientId)
              .map((c) => ({ value: c.id, label: c.name })),
          ]}
          value={clientId}
          onChange={(e) => patch({ client: e.target.value || null })}
        />
        <SearchInput
          searches="agreements"
          value={search}
          onChange={(next) => patch({ q: next || null })}
          className="shrink-0 grow basis-40 sm:w-52 sm:grow-0 sm:basis-auto"
        />
        <div className="sm:ml-auto">
          <ArchivedToggle
            noun="agreements"
            active={showArchived}
            count={archivedCount}
            onChange={(next) => patch({ archived: next ? '1' : null })}
          />
        </div>
      </div>

      {query.isError || retrying ? (
        // A skeleton that is ALSO what failure looks like is a fake loading
        // page: the request had already failed twice and the screen still said
        // "loading". The moment the first attempt fails we say so, name the
        // reason, and offer a manual retry while the automatic ones run.
        <ErrorState
          card
          title={retrying ? 'Still trying to load agreements' : 'Couldn’t load agreements'}
          message={
            retrying
              ? `${failureMessage ?? 'The request failed.'} Retrying automatically (attempt ${query.failureCount + 1})…`
              : failureMessage
          }
          retryLabel={retrying ? 'Retry now' : 'Try again'}
          onRetry={() => query.refetch()}
        />
      ) : query.isPending ? (
        <Card>
          <SkeletonTable rows={9} />
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={attention ? <ShieldCheck /> : <Briefcase />}
            title={
              attention
                ? 'Nothing needs attention'
                : filtered
                  ? 'No agreements match'
                  : 'No agreements yet'
            }
            description={
              attention
                ? 'No agreement in this view is losing money, past its lifetime budget or fee, or under a 30% margin.'
                : filtered
                  ? 'Try clearing the search or filters to see more agreements.'
                  : 'Create your first agreement to start tracking time, budgets, and billing.'
            }
            action={
              filtered ? (
                <Button variant="outline" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : (
                <Button onClick={() => setModalOpen(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  New agreement
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <>
          {/* Mobile: cards. The desktop table's columns cannot survive 390px —
              burn and margin, the whole point of this screen, fall off the edge.
              Same four figures as the table, Cost included. */}
          <div className="space-y-2.5 md:hidden">
            {mobileRows.map((p, i) => {
              const startsGroup = i === 0 || CLIENT_GROUP.key(mobileRows[i - 1]) !== CLIENT_GROUP.key(p);
              return (
                <Fragment key={p.id}>
                  {startsGroup && (
                    <h3 className="px-1 pt-2 text-body-sm font-bold text-secondary">
                      {CLIENT_GROUP.label(p)}
                    </h3>
                  )}
                  <AgreementMobileCard agreement={p} onSetBudget={() => setBudgetTarget(p)} />
                </Fragment>
              );
            })}
            <AgreementsTotalsCard totals={totals} />
          </div>

          {(() => {
            // Selection is measured against what is ON SCREEN — the same rule
            // every bulk act in this app follows. A hidden (filtered-out) row
            // cannot be part of the count the dialog states.
            const visible = new Set(rows.map((r) => r.id));
            const chosen = rows.filter((r) => selectedIds.has(r.id));
            const toArchive = chosen.filter((r) => r.isActive);
            const toRestore = chosen.filter((r) => !r.isActive);
            const act = (ids: string[], archived: boolean) =>
              archiveBatch.mutate(
                { ids, archived },
                {
                  onSuccess: ({ count }) => {
                    toast.success(
                      archived
                        ? `${count} agreement${count === 1 ? '' : 's'} archived.`
                        : `${count} agreement${count === 1 ? '' : 's'} restored.`,
                    );
                    setSelectedIds(new Set());
                    setConfirmingArchive(false);
                  },
                  onError: (err) =>
                    toast.error(err instanceof Error ? err.message : 'Nothing was changed.'),
                },
              );
            return (
              <>
                {chosen.length > 0 && (
                  <div className="mb-2 hidden items-center gap-3 rounded-lg border border-grey-200 bg-white px-3 py-2 shadow-card md:flex">
                    <span className="text-body-sm font-semibold text-secondary">
                      {chosen.length} selected
                    </span>
                    {toArchive.length > 0 && (
                      <Button size="sm" variant="outline" onClick={() => setConfirmingArchive(true)}>
                        Archive {toArchive.length}
                      </Button>
                    )}
                    {toRestore.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={archiveBatch.isPending}
                        onClick={() => act(toRestore.map((r) => r.id), false)}
                      >
                        Restore {toRestore.length}
                      </Button>
                    )}
                    {/* Many-to-many: every selected agreement onto ONE hub
                        project, one gesture — the two support agreements for a
                        client both point at the place the work happens. */}
                    <Button size="sm" variant="outline" onClick={() => setBulkLinkOpen(true)}>
                      Link {chosen.length} to hub
                    </Button>
                    <div className="flex-1" />
                    <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                      Clear
                    </Button>
                  </div>
                )}
                <AgreementsTable
                  className="hidden md:block"
                  rows={rows}
                  totals={totals}
                  showClient
                  groupByClient
                  sort={sort}
                  onSortChange={(next) => patch({ sort: next?.key ?? null, dir: next?.dir ?? null })}
                  onSetBudget={(p) => setBudgetTarget(p as AgreementRow)}
                  onRowClick={(p) => navigate(`/agreements/${p.id}`)}
                  rowActions
                  selected={selectedIds}
                  onToggleSelect={(id, checked) =>
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (checked) next.add(id);
                      else next.delete(id);
                      return next;
                    })
                  }
                  onToggleSelectAll={(checked) =>
                    setSelectedIds(checked ? visible : new Set())
                  }
                />
                <ConfirmDialog
                  open={confirmingArchive}
                  onOpenChange={setConfirmingArchive}
                  title={`Archive ${toArchive.length} agreement${toArchive.length === 1 ? '' : 's'}?`}
                  description={`They leave the pickers and the default lists immediately, keep every hour and invoice attached to them, and can be restored from the Archived view at any time.${toRestore.length > 0 ? ` The ${toRestore.length} already-archived selection${toRestore.length === 1 ? ' is' : 's are'} untouched.` : ''}`}
                  confirmLabel={`Archive ${toArchive.length}`}
                  destructive
                  onConfirm={() => act(toArchive.map((r) => r.id), true)}
                />
              </>
            );
          })()}
        </>
      )}

      <AgreementModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSaved={(id) => navigate(`/agreements/${id}`)}
      />
      <AgreementModal
        open={budgetTarget != null}
        onOpenChange={(open) => !open && setBudgetTarget(null)}
        agreement={budgetTarget ?? undefined}
      />
      <Modal
        open={bulkLinkOpen}
        onOpenChange={(open) => {
          if (!open) {
            setBulkChoice('');
            setBulkLinkOpen(false);
          }
        }}
        title="Link selected agreements to a hub project"
        description="Every selected agreement gets a link to the same hub project. Existing links are kept — a duplicate is a no-op, never an error."
        footer={
          <>
            <Button variant="ghost" onClick={() => setBulkLinkOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!bulkChoice}
              loading={bulkLink.isPending}
              onClick={() =>
                bulkLink.mutate(
                  { hubProjectId: bulkChoice, agreementIds: [...selectedIds] },
                  {
                    onSuccess: (result) => {
                      toast.success(
                        `${result.linked} agreement${result.linked === 1 ? '' : 's'} linked to the hub project`,
                      );
                      setBulkChoice('');
                      setBulkLinkOpen(false);
                      setSelectedIds(new Set());
                    },
                    onError: (error) =>
                      toast.error(error instanceof Error ? error.message : 'Could not link'),
                  },
                )
              }
            >
              Link {selectedIds.size}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <HubProjectPicker rows={hub.data?.projects ?? []} value={bulkChoice} onChange={setBulkChoice} />
          {hub.data?.connected === false && (
            <p className="text-body-sm text-status-late">
              The hub connection is down — nothing can be listed. Fix Settings → Hub first.
            </p>
          )}
        </div>
      </Modal>

    </div>
  );
}
